import { Action, Mutation } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import moment from 'moment';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { EdgeApi } from '@trialcard/apigateway.client';
import { ContactInfoType } from '@trialcard/enums';
import { CommonTools } from '../components/tools/commonTools';
import LayoutModule from './layout';
import EnrollmentModule from './enrollment';
import SectionModule from './section';
import PatientSupportServices from './patientSupportServices';
import ProgramModule from './program';
import { Settings } from '~/services/settings';
import { Field } from '~/types/docuSign/Fields';
import { IProduct } from '~/types/serviceTypes';

interface IRecipient {
    name: string;
    email: string;
    role: string;
}

interface ICreateEnvelopeRequest {
    programId: number | null;
    documentType: string | null;
    brandId: string;
    senderId: string;
    templateId: string;
    replyEmailAddressOverride: string;
    expireInDays: number;
    emailSubject: string;
    emailBody: string;
    send: boolean;
    embedded: boolean;
    recipients: IRecipient[];
    fields: Field[];
    externalPartyCode?: string;
    externalProgramCode?: string;
}

@InjectModule({ stateFactory: true }, module)
export default class DocusignModule extends InjectVuexModule {
    @Resolve
    settings!: Settings;
    @Resolve
    layout!: LayoutModule;
    @Resolve
    enrollment!: EnrollmentModule;
    @Resolve
    edgeApi!: EdgeApi;

    @Resolve
    section!: SectionModule;

    @Resolve
    program!: ProgramModule;

    @Resolve
    patientSupportServices!: PatientSupportServices;

    envelopeId = '';
    recipientViewURL = '';
    eventType: string | null = null;

    L_DRIVE_EXTERNAL_PARTY_CODE = 'FileDrop';
    L_DRIVE_EXTERNAL_PROGRAM_CODE = 'Gilead DMR';

    structureCreateEnvelopeRequest() {

        const programId = this.program.getProgramId();

        const request: ICreateEnvelopeRequest = {
            programId,
            documentType: this.getDocumentType(),
            brandId: this.getBrandId(),
            senderId: this.getSenderId(),
            templateId: this.getTemplateId(),
            replyEmailAddressOverride: 'no-reply@docusign.net',
            expireInDays: +this.settings.expireInDays,
            emailSubject: ' ',
            emailBody: ' ',
            send: true,
            embedded: true,
            recipients: [
                {
                    name: this.enrollment.patientInformationData.firstName + ' ' + this.enrollment.patientInformationData.lastName,
                    email: this.enrollment.patientInformationData.emailAddresses.length > 0 && this.enrollment.patientInformationData.emailAddresses[0].address ? this.enrollment.patientInformationData.emailAddresses[0].address : '',
                    role: 'Patient',
                }
            ],
            fields:  this.getFields() as Field[]
        };

        if(this.program.getPurchasedProductDetails().dropTo === 'L-DRIVE') {
            request.externalPartyCode = this.L_DRIVE_EXTERNAL_PARTY_CODE;
            request.externalProgramCode = this.L_DRIVE_EXTERNAL_PROGRAM_CODE;
        }

        return {request, programId};
    }

    structureRecipientViewUrlRequest() {
        const programId = this.program.getProgramId();
        const request = {
            programId,
            envelopeId: this.envelopeId,
            email: this.enrollment.patientInformationData.emailAddresses.length > 0 && this.enrollment.patientInformationData.emailAddresses[0].address ? this.enrollment.patientInformationData.emailAddresses[0].address : '',
            name: this.enrollment.patientInformationData.firstName + ' ' + this.enrollment.patientInformationData.lastName,
            returnUrl: CommonTools.getDocusignRelayUrl(this.settings),
        };
        return {request, programId};
    }

    @Action({})
    async createEnvelope() {
        let isSuccessful = false;
        const {request, programId} = this.structureCreateEnvelopeRequest();
        try {

            const axios: AxiosInstance = (this.edgeApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/signature/v1/EmailSignatureTemplate/CreateEnvelope',
                method: 'POST',
                data: request,
                headers: {
                    'x-program-id': programId,
                },
            };
            await Promise.all([
                (this.edgeApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            const { data } = await axios.request(axiosRequest);
            const envelopeId = data && data.data && data.data.envelopeId || '';
            if (!envelopeId) throw new Error('Envelope creation failed')
            this.setEnvelopeId(envelopeId);
            isSuccessful = data.data && data.success;
        } catch(error) {
            console.error('Error from creating Docusign envelope:',error);
        }
        return isSuccessful
    }

    @Action({})
    async requestURL() {
        let isSuccessful = false;
        try {
            const {request, programId} = this.structureRecipientViewUrlRequest();
            const axios: AxiosInstance = (this.edgeApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/signature/v1/RecipientView/RequestURL',
                method: 'POST',
                data: request,
                headers: {
                    'x-program-id': programId,
                },
            };
            await Promise.all([
                (this.edgeApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
                (this.edgeApi as any).authentications.Bearer.applyToRequest(axiosRequest),
                (this.edgeApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            const { data } = await axios.request(axiosRequest);
          const recipientViewURL = data && data.data && data.data.url || '';
            if (!recipientViewURL) throw new Error('Getting URL failed')
             this.setRecipientViewURL(recipientViewURL);
            isSuccessful = data.data && data.success;

        } catch(error) {
            console.error('Error from requesting recipient view URL:',error);
        }
        return isSuccessful;
    }

    @Mutation
    setEnvelopeId(envelopeId: string) {
        this.envelopeId = envelopeId;
    }

    @Mutation
    setRecipientViewURL(recipientViewURL: string) {
        this.recipientViewURL = recipientViewURL;
    }

    @Mutation
    setEventType(eventType: string | null) {
        this.eventType = eventType;
    }

    getBrandId() {
        const product : IProduct | undefined = this.program.products.find((product: IProduct) => product.ndc === this.program.purchasedProduct);
        if(!product) return '';
        return product.program === 'Gilead' ? this.settings.gileadBrandId : this.settings.aseguaBrandId;
    }

    getSenderId() {
        const product : IProduct | undefined = this.program.products.find((product: IProduct) => product.ndc === this.program.purchasedProduct);
        if(!product) return '';
        return product.program === 'Gilead' ? this.settings.gileadSenderId : this.settings.aseguaSenderId;
    }

    getTemplateId() {
        const product : IProduct | undefined = this.program.products.find((product: IProduct) => product.ndc === this.program.purchasedProduct);
        if(!product) return '';
        return product.program === 'Gilead' ? this.settings.gileadTemplateId : this.settings.aseguaTemplateId;
    }

    getDocumentType() {
        return this.settings.documentType.toLowerCase() === 'null' ? null : this.settings.documentType;
    }

    getFields(){
        const product : IProduct | undefined = this.program.products.find((product: IProduct) => product.ndc === this.program.purchasedProduct);
        if(!product) return [];
        return [...this.getCommonFields(product), ...(product.program === 'Gilead' ? this.getGileadFields() : this.getAseguaFields())];
    }

    getCommonFields(product: IProduct){
        return [
            {
                name: 'Pat_Full_Name',
                value: (this.enrollment.patientInformationData.firstName || '') + ' ' + (this.enrollment.patientInformationData.lastName || ''),
            },
            {
                name: 'Pat_Last_Name',
                value: this.enrollment.patientInformationData.lastName || '',
            },
            {
                name: 'Pat_First_Name',
                value: this.enrollment.patientInformationData.firstName || '',
            },
            {
                name: 'Drug_Name',
                value: product.name ? product.name.replace(/<[^>]*>/g, '') : '',
            },
            {
                name: 'Member_Number',
                value: this.program.copaySavingsCardId || '',
            },
            {
                name: 'Pat_Email',
                value: this.enrollment.patientInformationData.emailAddresses.length > 0 && this.enrollment.patientInformationData.emailAddresses[0].address ? this.enrollment.patientInformationData.emailAddresses[0].address : '',
            },
            // {
            //     name: 'Pat_MI',
            //     value: '',
            // },
            // {
            //     name: 'Pat_Mobile_Ph',
            //     value: '',
            // },
            // {
            //     name: 'Pat_DOB',
            //     value: '',
            // },
            // {
            //     name: 'Pat_Address1',
            //     value: '',
            // },
            // {
            //     name: 'Pat_Address2',
            //     value: '',
            // },
            // {
            //     name: 'Pat_City',
            //     value: '',
            // },
            // {
            //     name: 'Pat_State',
            //     value: '',
            // },
            // {
            //     name: 'Pat_ZIP',
            //     value: '',
            // },
            // {
            //     name: 'Commercial_ins_true',
            //     value: 'false',
            // },
            // {
            //     name: 'Commercial_ins_false',
            //     value: 'false',
            // },
            // {
            //     name: 'PBM_Payor',
            //     value: '',
            // },
            // {
            //     name: 'Qty_Dispensed',
            //     value: '',
            // },
            // {
            //     name: 'Days_Supply',
            //     value: '',
            // },
        ]
    }
    getAseguaFields(){
        return [
            // {
            //     name: 'PBM_Policy',
            //     value: '',
            // },
        ]
    }
    getGileadFields(){
        return [
            // {
            //     name: 'MD_Medication',
            //     value: 'false',
            // },
            // {
            //     name: 'Pharmacy_Medication',
            //     value: 'false',
            // },
            // {
            //     name: 'MED_Payor',
            //     value: '',
            // },
        ]
    }
}
